// Shared dashboard parts. Each follows its JAL Core component spec
// (jal-design-system references/components.md) in JAL tokens; styles live in
// ../styles.css under the dash- prefix. Icons: koboyo is not connected in
// this build, so a state is named by its title text (and the system check
// glyph where the spec has a check); no third icon source is drawn.
import { Fragment, useEffect, useId, useRef, useState, type ReactNode } from "react";
// glyphs.tsx is not re-exported from the package index; deep import, read only.
import { CheckGlyph } from "@bot-whatsapp/ui/src/kit/glyphs";
import { formatDateTime, formatNumber } from "./format";
import type { Reveal } from "./reveal";
import type { Submission, SubmissionPii } from "./types";

/** The screen's page header: one h1 and one meta line (two text tiers),
 *  closed by one full-width hairline like a spec sheet's title block. */
export function ScreenHeader({ id, title, meta, live }: { id: string; title: string; meta: ReactNode; live?: boolean }) {
  return (
    <header className="dash-head kit-full">
      <h1 id={id} className="kit-heading">
        {title}
      </h1>
      <p className="kit-body dash-head-meta" aria-live={live ? "polite" : undefined}>
        {meta}
      </p>
    </header>
  );
}

export type Tone = "info" | "success" | "warning" | "danger";

/** Carbon inline notification on the JAL notice chrome: tonal surface, one
 *  full hairline on all four sides, a title that names the state. */
export function Notice({ tone, title, children, action }: { tone: Tone; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="dash-notice" data-tone={tone} role={tone === "danger" ? "alert" : "status"}>
      <div className="dash-notice-body">
        <p className="dash-notice-title">{title}</p>
        {children ? <p className="dash-notice-text">{children}</p> : null}
      </div>
      {action ? <div className="dash-notice-action">{action}</div> : null}
    </div>
  );
}

/** Badge spec: a text label on a tonal pill, never a bare dot. */
export function StatusBadge({ tone, children }: { tone: "neutral" | "success" | "info" | "danger"; children: ReactNode }) {
  return (
    <span className="dash-badge" data-tone={tone}>
      {children}
    </span>
  );
}

export interface ChipOption<T extends string> {
  value: T;
  label: string;
}

/** Filter chips, single select (Chips spec). Chosen over the segmented button
 *  because four segments clip their labels at 320px (law: no clipped text);
 *  below 480px four chips sit 2 by 2, so no chip is orphaned on a line. */
export function FilterChips<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: ChipOption<T>[];
  onChange: (value: T) => void;
}) {
  return (
    <div className="dash-chips" role="toolbar" aria-label={label} data-count={options.length}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button key={o.value} type="button" className="dash-chip" aria-pressed={on} onClick={() => onChange(o.value)}>
            {on ? <CheckGlyph className="dash-chip-check" /> : null}
            <span>{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/** The per-record reveal toggle (JEV ui.component_recipe outline_toggle): a
 *  44px outline control with aria-pressed, one locked width for both labels
 *  so the row never moves, a layer-2 fill while pressed. */
export function RevealButton({
  shown,
  loading,
  onToggle,
  subject,
}: {
  shown: boolean;
  /** The reveal call is in flight: the label reads "Memuat" in the same lane. */
  loading?: boolean;
  onToggle: () => void;
  subject: string;
}) {
  return (
    <button
      type="button"
      className="btn-secondary dash-reveal"
      aria-pressed={shown}
      aria-busy={loading || undefined}
      aria-label={`${shown ? "Samarkan" : "Tampilkan"} data ${subject}`}
      onClick={onToggle}
    >
      {loading ? "Memuat" : shown ? "Samarkan" : "Tampilkan"}
    </button>
  );
}

/** A masked value that swaps for the full one in place: mono, on a lane as
 *  wide as the longer form, so nothing beside it shifts. The new form fades
 *  in (motion tier 1, opacity only). */
export function MaskedValue({ full, masked, kind }: { full: string | undefined; masked: string; kind: "nik" | "phone" }) {
  const shown = full !== undefined;
  return (
    <span key={shown ? "full" : "masked"} className="kit-num dash-val" data-kind={kind} data-shown={shown || undefined}>
      {full !== undefined ? <Grouped parts={kind === "nik" ? groupNik(full) : groupPhone(full)} /> : masked}
    </span>
  );
}

/** NIK read in its three fields, 6 6 4: "320101 450892 0003". */
export function groupNik(nik: string): string[] {
  return nik.length === 16 ? [nik.slice(0, 6), nik.slice(6, 12), nik.slice(12)] : [nik];
}

/** "+6281234567789" read as "+62 8123 4567 789": the country code, then fours. */
export function groupPhone(phone: string): string[] {
  if (!phone.startsWith("+62")) return [phone];
  const rest = phone.slice(3);
  const parts = ["+62"];
  for (let i = 0; i < rest.length; i += 4) parts.push(rest.slice(i, i + 4));
  return parts;
}

/** Groups for display only: the gap is a margin, not a character, so a
 *  selection copies the raw value with no spaces in it. */
function Grouped({ parts }: { parts: string[] }) {
  return (
    <>
      {parts.map((p, i) => (
        <span key={i} className="dash-grp">
          {p}
        </span>
      ))}
    </>
  );
}

/** "Salin kode": copies the raw value, then says "Tersalin" in words for a
 *  moment. When the browser refuses the clipboard the button stays and one
 *  line under it says to read the code out instead. */
export function CopyCodeButton({ value }: { value: string }) {
  const [state, setState] = useState<"done" | "failed" | null>(null);
  useEffect(() => {
    if (!state) return;
    const id = setTimeout(() => setState(null), 2400);
    return () => clearTimeout(id);
  }, [state]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setState("done");
      return;
    } catch {
      // The async clipboard is blocked (no permission, not a secure origin):
      // fall back to a selected textarea and the copy command.
    }
    const area = document.createElement("textarea");
    area.value = value;
    area.setAttribute("readonly", "");
    area.className = "kit-visually-hidden";
    document.body.appendChild(area);
    area.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch {
      ok = false;
    }
    area.remove();
    setState(ok ? "done" : "failed");
  }

  return (
    <div className="dash-copy-wrap">
      <button type="button" className="btn-secondary dash-copy" data-state={state === "done" ? "success" : undefined} onClick={copy}>
        {state === "done" ? <CheckGlyph className="dash-copy-icon" /> : null}
        <span>{state === "done" ? "Tersalin" : "Salin kode"}</span>
      </button>
      <p className="dash-copy-note" role="status">
        {state === "failed" ? "Browser menolak salin, bacakan kodenya." : state === "done" ? <span className="kit-visually-hidden">Kode tersalin.</span> : ""}
      </p>
    </div>
  );
}

/** Submission records on one fixed column grid, shared by Ringkasan and
 *  Kiriman: a hairline DataTable from 1024 (column rules, one header row),
 *  stacked records below. Pass `reveal` to add the per-row reveal; a row
 *  shows full values only from the reveal route's answer. */
export function SubmissionRecords({
  items,
  describedBy,
  reveal,
  onDelete,
  onDetail,
}: {
  items: Submission[];
  describedBy?: string;
  reveal?: Reveal<SubmissionPii>;
  /** Shows a Hapus control per row (Kiriman only); the caller confirms first. */
  onDelete?: (s: Submission) => void;
  /** Shows an Ekstraksi control per row: the full NIK extraction dialog. */
  onDetail?: (s: Submission) => void;
}) {
  const onToggle = reveal?.toggle;
  const shownOf = (id: string) => Boolean(reveal?.shown.has(id));
  const fullOf = (id: string) => (reveal?.shown.has(id) ? reveal.values.get(id) : undefined);
  const loadingOf = (id: string) => Boolean(reveal?.loading.has(id));
  const errorOf = (id: string) => reveal?.errors.get(id);
  return (
    <>
      <div className="dash-table-box">
        <table className="dash-table" aria-describedby={describedBy}>
          <thead>
            <tr>
              <th scope="col" className="dash-col-time">Waktu</th>
              <th scope="col">Nama</th>
              <th scope="col" className="dash-col-nik">NIK</th>
              <th scope="col" className="dash-col-phone">Nomor</th>
              <th scope="col">Pengirim</th>
              {onToggle ? (
                <th scope="col" className={onDelete ? "dash-col-row-actions" : "dash-col-reveal"}>
                  {onDelete ? "Aksi" : "Data"}
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {items.map((s) => {
              const shown = shownOf(s.id);
              const full = fullOf(s.id);
              const error = errorOf(s.id);
              return (
                <Fragment key={s.id}>
                <tr data-revealed={shown || undefined} aria-busy={loadingOf(s.id) || undefined}>
                  <td className="dash-nowrap dash-time">{formatDateTime(s.receivedAt)}</td>
                  <td className="dash-strong">{s.name}</td>
                  <td className="dash-nowrap">
                    <MaskedValue kind="nik" full={full?.nik} masked={s.nikMasked} />
                  </td>
                  <td className="dash-nowrap">
                    <MaskedValue kind="phone" full={full?.phone} masked={s.phoneMasked} />
                  </td>
                  <td>{s.senderLabel ?? "Tanpa label"}</td>
                  {onToggle ? (
                    <td className="dash-cell-action">
                      <span className="dash-cell-buttons">
                        <RevealButton shown={shown} loading={loadingOf(s.id)} onToggle={() => onToggle(s.id)} subject={s.name} />
                        {onDetail ? (
                          <button type="button" className="btn-secondary" onClick={() => onDetail(s)} aria-label={`Ekstraksi NIK ${s.name}`}>
                            Ekstraksi
                          </button>
                        ) : null}
                        {onDelete ? (
                          <button type="button" className="btn-secondary dash-danger-quiet" onClick={() => onDelete(s)} aria-label={`Hapus kiriman ${s.name}`}>
                            Hapus
                          </button>
                        ) : null}
                      </span>
                    </td>
                  ) : null}
                </tr>
                {error ? (
                  <tr className="dash-error-row">
                    <td colSpan={6}>
                      <Notice tone="danger" title="Data tidak dibuka">
                        {error}
                      </Notice>
                    </td>
                  </tr>
                ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <ul className="dash-rows dash-stack">
        {items.map((s) => {
          const shown = shownOf(s.id);
          const full = fullOf(s.id);
          const error = errorOf(s.id);
          return (
            <li key={s.id} className="dash-row dash-record" data-revealed={shown || undefined} aria-busy={loadingOf(s.id) || undefined}>
              <p className="dash-row-title">{s.name}</p>
              {onToggle ? (
                <div className="dash-record-action">
                  <RevealButton shown={shown} loading={loadingOf(s.id)} onToggle={() => onToggle(s.id)} subject={s.name} />
                </div>
              ) : (
                <p className="dash-record-action dash-row-aside dash-time">{formatDateTime(s.receivedAt)}</p>
              )}
              <dl className="dash-pairs">
                <div>
                  <dt>NIK</dt>
                  <dd>
                    <MaskedValue kind="nik" full={full?.nik} masked={s.nikMasked} />
                  </dd>
                </div>
                <div>
                  <dt>Nomor</dt>
                  <dd>
                    <MaskedValue kind="phone" full={full?.phone} masked={s.phoneMasked} />
                  </dd>
                </div>
                {onToggle ? (
                  <div>
                    <dt>Waktu</dt>
                    <dd className="dash-time">{formatDateTime(s.receivedAt)}</dd>
                  </div>
                ) : null}
                <div>
                  <dt>Pengirim</dt>
                  <dd>{s.senderLabel ?? "Tanpa label"}</dd>
                </div>
              </dl>
              {error ? (
                <Notice tone="danger" title="Data tidak dibuka">
                  {error}
                </Notice>
              ) : null}
              {onDetail || onDelete ? (
                <div className="dash-record-delete">
                  {onDetail ? (
                    <button type="button" className="btn-secondary" onClick={() => onDetail(s)} aria-label={`Ekstraksi NIK ${s.name}`}>
                      Ekstraksi
                    </button>
                  ) : null}
                  {onDelete ? (
                    <button type="button" className="btn-secondary dash-danger-quiet" onClick={() => onDelete(s)} aria-label={`Hapus kiriman ${s.name}`}>
                      Hapus
                    </button>
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </>
  );
}

/** A compact count strip (JEV ui.component_recipe count_block
 *  hairline_strip): one hairline panel split by 1px column rules, each cell
 *  a label over a mono tabular figure at the kit stat step, never the
 *  signal color. A value not loaded yet holds a skeleton, never a fake 0. */
export function CountStrip({ label, items }: { label: string; items: { id: string; label: string; value: number | undefined }[] }) {
  return (
    <dl className="dash-counts kit-full" aria-label={label} data-count={items.length}>
      {items.map((item) => (
        <div key={item.id} className="dash-count">
          <dt>{item.label}</dt>
          <dd className="kit-bento-stat">
            {item.value === undefined ? (
              <span className="dash-skeleton" data-size="figure" data-shown="" aria-hidden="true" />
            ) : (
              <span className="kit-num">{formatNumber(item.value)}</span>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** A literal the operator types or matches (DASHBOARD_TOKEN, 482913). */
export function CodeChip({ children }: { children: ReactNode }) {
  return <code className="dash-code-chip">{children}</code>;
}

/** EmptyState spec: a title naming the state, one sentence, one action. */
export function EmptyState({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <div className="dash-empty">
      <p className="kit-title">{title}</p>
      <p className="kit-body">{body}</p>
      {action ? <div className="dash-empty-action">{action}</div> : null}
    </div>
  );
}

/** A region-level load failure: the danger notice with a retry. */
export function LoadError({ what, onRetry }: { what: string; onRetry: () => void }) {
  return (
    <Notice
      tone="danger"
      title={`${what} tidak dapat dimuat`}
      action={
        <button type="button" className="btn-secondary" onClick={onRetry}>
          Coba lagi
        </button>
      }
    >
      Periksa koneksi ke server, lalu coba lagi.
    </Notice>
  );
}

/** Skeleton rows matching a record list, shown only after the 300ms wait. */
export function RowsSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <ul className="dash-rows" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <li key={i} className="dash-row dash-skeleton-row">
          <span className="dash-skeleton" data-size="title" />
          <span className="dash-skeleton" data-size="meta" />
        </li>
      ))}
    </ul>
  );
}

export interface ConfirmRequest {
  title: string;
  body: string;
  confirmLabel: string;
  /** danger (default) for destructive actions, primary for restoring. */
  tone?: "danger" | "primary";
  run: () => Promise<void>;
}

/** AlertDialog spec: native dialog.showModal, title names the object, one
 *  sentence on the consequence, ghost Batal then the destructive confirm.
 *  A bottom sheet below 640px, a centered panel from 640px, scrim, no shadow. */
export function ConfirmDialog({
  request,
  busy,
  error,
  onConfirm,
  onClose,
}: {
  request: ConfirmRequest | null;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const bodyId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog || typeof dialog.showModal !== "function") return;
    if (request && !dialog.open) dialog.showModal();
    if (!request && dialog.open) dialog.close();
  }, [request]);

  return (
    <dialog
      ref={ref}
      className="dash-dialog"
      role="alertdialog"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      onCancel={(e) => {
        e.preventDefault();
        if (!busy) onClose();
      }}
    >
      {request ? (
        <div className="dash-dialog-inner">
          <h2 id={titleId} className="kit-title">
            {request.title}
          </h2>
          <p id={bodyId} className="kit-body">
            {request.body}
          </p>
          {error ? <Notice tone="danger" title="Tindakan gagal">{error}</Notice> : null}
          <div className="dash-dialog-actions">
            <button type="button" className="btn-ghost" onClick={onClose} disabled={busy} autoFocus>
              Batal
            </button>
            <button
              type="button"
              className={request.tone === "primary" ? "btn-primary" : "dash-danger"}
              onClick={onConfirm}
              aria-busy={busy || undefined}>
              {request.confirmLabel}
            </button>
          </div>
        </div>
      ) : null}
    </dialog>
  );
}
